import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { SelectBox } from "../components/common/SelectBox";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import { userRolesNewUser } from "../components/userManagement/constants";
import UserRoles from "../components/userManagement/UserRoles";
import { FormatString } from "../helpers/stringHelpers";
import TranslationContext from "../providers/TranslationContext";
import { ValidationProvider } from "../providers/ValidationContext";
import { addNotification } from "../redux/actions";
import {
  apiGetUserDetail,
  apiUserCreateOrUpdate,
} from "../services/UsersService";
import { validateForm } from "../validation/formValidation";
import { model } from "../validation/models/user";

import PopupOperationsBar from "./PopupOperationsBar";

// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
  sellers: any;
  isAdmin: boolean;
  sellerId: number;
  isUserAdmin: any;
  userInfo: any;
}

const UserPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));
  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);

  const isEditMode = props.id ? true : false;

  const dispatch = useDispatch();

  useEffect(() => {

    loadData();

  }, []);

  const loadData = async () => {

    if (props.id) {
      apiGetUserDetail(props.id).then((responseData: any) => {

        setFormData({ ...responseData.data, sellerId: props.sellerId });

        setLoading(false);
      });
    } else {

      var initRoles = cloneDeep(userRolesNewUser);
      setFormData({ sellerId: props.sellerId, userRoles: initRoles });
      setLoading(false);
    }
  };

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiUserCreateOrUpdate(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.firstname, formData.lastname), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          let detail = err.response.data;
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.firstname, formData.lastname) + `Error: ${detail}`, type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiUserCreateOrUpdate(formData)
        .then((responseData) => {


          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.firstname, formData.lastname), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          let detail = err.response.data;
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.firstname, formData.lastname) + `Error: ${detail}`, type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };

  const handleRolesChange = (userRoles) => {
    setFormData({ ...formData, userRoles: userRoles });
  };

  return (
    <PopupContent header={props.id ? t('EditLabel') : t('CreateLabel')}>
      <ValidationProvider value={validationModel}>
        <>
          <TextBox
            dataField="firstname"
            defaultValue={formData.firstname}
            formData={formData}
            setStateMethod={setFormData}
            id="firstname"
            label={t('Labels.firstname')}
            placeholder={t('Labels.firstnamePlaceholder')}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
          />
          <TextBox
            dataField="lastname"
            defaultValue={formData.lastname}
            formData={formData}
            setStateMethod={setFormData}
            id="lastname"
            label={t('Labels.lastname')}
            placeholder={t('Labels.lastnamePlaceholder')}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
          />
          <TextBox
            dataField="email"
            defaultValue={formData.email}
            formData={formData}
            setStateMethod={setFormData}
            id="email"
            label={t('Labels.email')}
            placeholder={t('Labels.emailPlaceholder')}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
          />
          <SelectBox
            dataField="sellerId"
            value={formData.sellerId}
            formData={formData}
            label={t('Labels.seller')}
            options={props.sellers}
            placeholder={t('Labels.sellerPlaceholder')}
            setStateMethod={setFormData}
            isDisabled={!props.isUserAdmin}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
          />
          <TextBox
            dataField="login"
            defaultValue={formData.login}
            formData={formData}
            setStateMethod={setFormData}
            id="login"
            label={t('Labels.login')}
            placeholder={t('Labels.loginPlaceholder')}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
          />
          {props.isAdmin && <TextBox
            dataField="password"
            defaultValue={formData.password}
            formData={formData}
            setStateMethod={setFormData}
            isPassword={true}
            id="password"
            label={t('Labels.password')}
            placeholder={t('Labels.passwordPlaceholder')}
            customLeftCols={'col-sm-2'}
            customRightCols={'col-sm-10'}
            autoComplete={'new-password'}
          />}
          <TextArea
            id="noteInternal"
            label={t('Labels.note')}
            placeholder={t('Labels.notePlaceholder')}
            rows={4}
            dataField="noteInternal"
            value={formData.noteInternal}
            formData={formData}
            setStateMethod={setFormData}
            customColClassForLabel={'col-sm-2'}
            customColClassForValue={'col-sm-10'}
          />

          <UserRoles
            userInfo={props.userInfo}
            isUserAdmin={props.isUserAdmin}
            
            data={formData.userRoles}
            enabled={true}
            onRolesChange={handleRolesChange}
          />
          <PopupOperationsBar
            handleAdding={onAddingClick}
            handleCancellation={onCancelClick}
            handleEditing={onEditingClick}
            id={props.id}
          />
        </>
      </ValidationProvider>
    </PopupContent>
  );
};

export default UserPopup;
